import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Paper, Skeleton, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid, GridActionsCellItem, GridToolbar } from "@mui/x-data-grid";
import httpClient, { extractApiError } from "../api/httpClient";
import { dataGridSx, PageHeader, PagePanel, PageShell } from "../components/PageLayout";
import EmployeeFormModal from "../components/EmployeeFormModal";
import { useUI } from "../contexts/UIContext";

export default function Dipendenti() {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [locali, setLocali] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const { showNotification } = useUI();

    const normalizeEmployee = useCallback((employee) => ({
        id: employee?.id,
        nome: employee?.nome || "",
        telefono: employee?.telefono || "",
        mansione: employee?.mansione || "",
        localeId: employee?.localeId ?? employee?.locale?.id ?? "",
        localeNome: employee?.locale?.nome || employee?.localeNome || "-",
    }), []);

    const loadEmployees = useCallback(async () => {
        setLoading(true);
        try {
            const [employeesResponse, localiResponse] = await Promise.all([
                httpClient.get("/dipendenti"),
                httpClient.get("/locali"),
            ]);
            setRows((employeesResponse?.data || []).map(normalizeEmployee));
            setLocali(localiResponse?.data || []);
        } catch (error) {
            showNotification(extractApiError(error), "error");
        } finally {
            setLoading(false);
        }
    }, [normalizeEmployee, showNotification]);

    useEffect(() => {
        loadEmployees();
    }, [loadEmployees]);

    const handleSave = async (employeePayload) => {
        try {
            const rawLocaleId = employeePayload.localeId ?? employeePayload.locale?.id ?? "";
            const payload = {
                nome: employeePayload.nome,
                telefono: employeePayload.telefono || null,
                mansione: employeePayload.mansione,
                localeId: rawLocaleId !== "" && rawLocaleId !== null && rawLocaleId !== undefined ? Number(rawLocaleId) : null,
            };

            if (employeePayload.id) {
                await httpClient.put(`/dipendenti/${employeePayload.id}`, payload);
                showNotification("Dipendente aggiornato", "success");
            } else {
                await httpClient.post("/dipendenti", payload);
                showNotification("Dipendente creato", "success");
            }
            setModalOpen(false);
            setSelectedEmployee(null);
            await loadEmployees();
        } catch (error) {
            showNotification(extractApiError(error), "error");
        }
    };

    const deleteEmployee = async (id) => {
        try {
            await httpClient.delete(`/dipendenti/${id}`);
            showNotification("Dipendente eliminato", "success");
            await loadEmployees();
        } catch (error) {
            showNotification(extractApiError(error), "error");
        }
    };

    const columns = useMemo(
        () => [
            { field: "id", headerName: "ID", width: 90 },
            { field: "nome", headerName: "Nome", minWidth: 140, flex: 1 },
            { field: "telefono", headerName: "Telefono", minWidth: 180, flex: 1 },
            { field: "mansione", headerName: "Mansione", minWidth: 140, flex: 0.8 },
            { field: "localeNome", headerName: "Locale predefinito", minWidth: 180, flex: 1 },
            {
                field: "actions",
                type: "actions",
                headerName: "Azioni",
                width: 110,
                getActions: (params) => [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Modifica"
                        onClick={() => {
                            setSelectedEmployee(params.row);
                            setModalOpen(true);
                        }}
                    />,
                    <GridActionsCellItem icon={<DeleteIcon />} label="Elimina" onClick={() => deleteEmployee(params.id)} />,
                ],
            },
        ],
        []
    );

    return (
        <PageShell>
            <PageHeader
                title="Gestione dipendenti"
                subtitle="Anagrafica, ruoli e costo orario con lo stesso impianto visivo editoriale della pagina turni."
                actions={
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setSelectedEmployee(null);
                            setModalOpen(true);
                        }}
                    >
                        Nuovo dipendente
                    </Button>
                }
            />

            <PagePanel tone="cool" sx={{ p: 1.5 }}>
                {loading ? (
                    <Skeleton variant="rounded" height={520} />
                ) : (
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        autoHeight
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50]}
                        sx={dataGridSx}
                        slots={{ toolbar: GridToolbar }}
                    />
                )}
            </PagePanel>

            <EmployeeFormModal
                open={modalOpen}
                employee={selectedEmployee}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedEmployee(null);
                }}
                onSubmit={handleSave}
                locali={locali}
            />
        </PageShell>
    );
}
